import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

const PERMISSION_OPTIONS = [
  { value: 'no_access', label: 'No Access' },
  { value: 'view_only', label: 'View Only' },
  { value: 'view_print', label: 'View + Print' },
  { value: 'view_download', label: 'View + Download' },
  { value: 'view_download_print', label: 'View + Download + Print' },
  { value: 'redacted', label: 'Redacted View' },
];

export default function InviteUserModal({ open, onClose, dataRoomId, onInvited }) {
  const [roles, setRoles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role_id: '',
    expires_at: '',
  });
  const [folderPerms, setFolderPerms] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      Promise.all([
        dataRoomAPI.listRoles(dataRoomId),
        dataRoomAPI.listFolders(dataRoomId),
      ]).then(([rr, fr]) => {
        setRoles(rr.data.data || []);
        setFolders(fr.data.data || []);
      });
    } else {
      setForm({ name: '', email: '', role_id: '', expires_at: '' });
      setFolderPerms({});
    }
  }, [open, dataRoomId]);

  if (!open) return null;

  const roots = folders.filter(f => !f.parent_id).sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role_id) {
      alert('Name, email and role are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        folder_permissions: Object.keys(folderPerms).length ? folderPerms : null,
      };
      await dataRoomAPI.inviteExternalUser(dataRoomId, payload);
      onInvited && onInvited();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to invite user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="invite-modal">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Invite External User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="close-invite">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              required
              data-testid="invite-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              required
              data-testid="invite-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select
              value={form.role_id}
              onChange={e => setForm({ ...form, role_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              required
              data-testid="invite-role"
            >
              <option value="">Select role...</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.default_permission.replace(/_/g, ' ')})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access Expiry (optional)</label>
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={e => setForm({ ...form, expires_at: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="invite-expiry"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for no automatic expiry</p>
          </div>

          {roots.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">Folder Permission Overrides (optional)</p>
              <p className="text-xs text-gray-500 mb-2">If not set, role default permission applies.</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {roots.map(f => (
                  <div key={f.id} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-700 truncate flex-1">{f.name}</span>
                    <select
                      value={folderPerms[f.id] || ''}
                      onChange={e => {
                        const v = e.target.value;
                        setFolderPerms(prev => {
                          const next = { ...prev };
                          if (v) next[f.id] = v; else delete next[f.id];
                          return next;
                        });
                      }}
                      className="border rounded px-2 py-1 text-xs"
                      data-testid={`folder-perm-${f.id}`}
                    >
                      <option value="">(Use role default)</option>
                      {PERMISSION_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              data-testid="submit-invite"
            >
              <Send size={14} className="mr-1" /> {submitting ? 'Inviting...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
